/**
 * Procedural glTF aircraft models for the live-aircraft TRUE-3D LOD (D2 §3.3,
 * 3D wave). At close zoom with few aircraft on screen the layer swaps the flat
 * billboard silhouette for a lightweight glTF model entity; a helicopter model
 * carries a looping rotor-spin animation.
 *
 * Why generate the glTF in code rather than ship .glb assets: the fleet only
 * needs three readable archetypes (jet / prop / helicopter), each must be
 * re-tinted to the operational role colour (water-bomber salmon, SAR orange …),
 * and we want zero binary blobs in the repo and zero network fetch at render
 * time. The geometry is deliberately low-poly — a recognisable top-down/oblique
 * shape, not a scale model. Materials use `KHR_materials_unlit` so the airframe
 * reads as one flat role-coloured shape at any sun angle / heading, matching the
 * billboard aesthetic it LODs from (and sidestepping per-vertex normals).
 *
 * ⚠️  PLACEHOLDER-QUALITY GEOMETRY remains flagged for design-council sign-off.
 *     The rotor spin is a deliberate motion choice OUTSIDE the operational motion
 *     budget (design-council-2026-06-05 Law 6), so it rides the shared
 *     `demo-mode` flag (`isDemoMode()`): the blades spin only in demo-mode
 *     (sales/demos) and the airframe ships with static rotors in operational
 *     mode. Gated in two places that must agree — the baked animation clip here,
 *     and `ModelGraphics.runAnimations` at the MapView call site.
 */
import type { AircraftKind } from './aircraftStyles';
import { isDemoMode } from './demoMode';

export type AircraftModelArchetype = 'jet' | 'prop' | 'helicopter';

/** Seconds for one full rotor revolution (visual cadence, not realistic RPM). */
const ROTOR_PERIOD_SECONDS = 0.6;
/** Revolutions baked into the clip so it reads as continuous even if the host
 *  does not loop entity-model animations. */
const ROTOR_REVOLUTIONS = 240;

export function archetypeForKind(kind: AircraftKind): AircraftModelArchetype {
  if (kind === 'heli') return 'helicopter';
  if (kind === 'jet') return 'jet';
  return 'prop';
}

// ── tiny mesh builder ────────────────────────────────────────────────────────
// Non-indexed triangle soup (each tri = 3 sequential vertices). glTF allows a
// primitive with no `indices`, which keeps the buffer to a single POSITION
// stream per mesh — no index accessor, no alignment juggling.

type Vec3 = readonly [number, number, number];

class Mesh {
  readonly verts: number[] = [];
  tri(a: Vec3, b: Vec3, c: Vec3): void {
    this.verts.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
  }
  /** A flat quad (a→b→c→d) as two triangles. */
  quad(a: Vec3, b: Vec3, c: Vec3, d: Vec3): void {
    this.tri(a, b, c);
    this.tri(a, c, d);
  }
  get vertexCount(): number {
    return this.verts.length / 3;
  }
}

// ── archetype geometry (nose +X, up +Z, span along ±Y; metres) ───────────────

interface PlaneDims {
  length: number; // nose→tail
  span: number; // wing tip→tip
  sweep: number; // 0 straight … 1 sharply swept
  finHeight: number;
}

function buildPlaneBody(d: PlaneDims): Mesh {
  const m = new Mesh();
  const L = d.length / 2;
  const span = d.span / 2;
  const bodyW = d.span * 0.05;
  // Fuselage (flat diamond, z=0).
  const nose: Vec3 = [L, 0, 0];
  const tail: Vec3 = [-L * 0.9, 0, 0];
  const midL: Vec3 = [-L * 0.1, bodyW, 0];
  const midR: Vec3 = [-L * 0.1, -bodyW, 0];
  m.tri(nose, midL, tail);
  m.tri(nose, tail, midR);
  // Wings (swept; tips trail by `sweep`).
  const wRootF: Vec3 = [L * 0.15, 0, 0];
  const wRootB: Vec3 = [-L * 0.25, 0, 0];
  const wTipL: Vec3 = [-L * d.sweep, span, 0];
  const wTipR: Vec3 = [-L * d.sweep, -span, 0];
  m.tri(wRootF, wTipL, wRootB);
  m.tri(wRootF, wRootB, wTipR);
  // Horizontal stabiliser.
  const hRootF: Vec3 = [-L * 0.7, 0, 0];
  const hRootB: Vec3 = [-L * 0.95, 0, 0];
  const hTipL: Vec3 = [-L * 0.9, span * 0.34, 0];
  const hTipR: Vec3 = [-L * 0.9, -span * 0.34, 0];
  m.tri(hRootF, hTipL, hRootB);
  m.tri(hRootF, hRootB, hTipR);
  // Vertical fin (XZ plane).
  m.tri([-L * 0.72, 0, 0], [-L * 0.95, 0, 0], [-L * 0.88, 0, d.finHeight]);
  return m;
}

function buildHeliBody(): Mesh {
  const m = new Mesh();
  const L = 7; // fuselage half-length
  const W = 2.4; // cabin half-width
  // Cabin (flat diamond).
  m.tri([L, 0, 0], [0, W, 0], [-L * 0.2, 0, 0]);
  m.tri([L, 0, 0], [-L * 0.2, 0, 0], [0, -W, 0]);
  // Tail boom.
  m.quad([-L * 0.2, 0.6, 0], [-L * 1.7, 0.25, 0], [-L * 1.7, -0.25, 0], [-L * 0.2, -0.6, 0]);
  // Tail fin (vertical).
  m.tri([-L * 1.5, 0, 0], [-L * 1.7, 0, 0], [-L * 1.6, 0, 2.2]);
  return m;
}

function buildRotor(): Mesh {
  const m = new Mesh();
  const R = 9; // blade radius
  const b = 0.5; // blade half-width
  // Two crossed blades (a "+") → spin is legible.
  m.quad([R, b, 0], [-R, b, 0], [-R, -b, 0], [R, -b, 0]);
  m.quad([b, R, 0], [-b, R, 0], [-b, -R, 0], [b, -R, 0]);
  return m;
}

const HELI_ROTOR_HEIGHT = 2.6; // rotor disc height above the cabin

// ── glTF assembly ────────────────────────────────────────────────────────────

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Base64-encode bytes without relying on btoa/Buffer (runs in browser + node). */
function base64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i]!;
    const b1 = i + 1 < bytes.length ? bytes[i + 1]! : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2]! : 0;
    out += B64[b0 >> 2];
    out += B64[((b0 & 3) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? B64[((b1 & 15) << 2) | (b2 >> 6)] : '=';
    out += i + 2 < bytes.length ? B64[b2 & 63] : '=';
  }
  return out;
}

function f32Bytes(values: number[]): Uint8Array {
  const buf = new Float32Array(values);
  return new Uint8Array(buf.buffer);
}

function min3(v: number[]): [number, number, number] {
  const r: [number, number, number] = [Infinity, Infinity, Infinity];
  for (let i = 0; i < v.length; i += 3)
    for (let k = 0; k < 3; k++) r[k] = Math.min(r[k]!, v[i + k]!);
  return r;
}
function max3(v: number[]): [number, number, number] {
  const r: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < v.length; i += 3)
    for (let k = 0; k < 3; k++) r[k] = Math.max(r[k]!, v[i + k]!);
  return r;
}

/** Quaternion (x,y,z,w) for a rotation of `rad` about +Z. */
function quatZ(rad: number): [number, number, number, number] {
  return [0, 0, Math.sin(rad / 2), Math.cos(rad / 2)];
}

interface ParsedColor {
  r: number;
  g: number;
  b: number;
}
function parseHex(hex: string): ParsedColor {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

interface BuiltGltf {
  /** glTF JSON object (exposed for tests). */
  json: unknown;
  /** `data:` URI a Cesium ModelGraphics can consume directly. */
  uri: string;
}

/**
 * Build a self-contained glTF (single embedded buffer) for one archetype, tinted
 * to `color`. Helicopters get a second `rotor` node with a looping Z-spin
 * animation — baked only in demo-mode (`demo`), so operational airframes ship
 * with static rotors and stay within the motion budget.
 */
function buildGltf(
  archetype: AircraftModelArchetype,
  color: string,
  demo: boolean,
): BuiltGltf {
  const body =
    archetype === 'helicopter'
      ? buildHeliBody()
      : buildPlaneBody(
          archetype === 'jet'
            ? { length: 24, span: 18, sweep: 0.85, finHeight: 4 }
            : { length: 18, span: 22, sweep: 0.25, finHeight: 3 },
        );
  const rotor = archetype === 'helicopter' ? buildRotor() : null;
  const animated = archetype === 'helicopter' && demo;

  // ── buffer sections (all Float32 → naturally 4-byte aligned) ──
  const sections: Uint8Array[] = [];
  const bufferViews: unknown[] = [];
  const accessors: unknown[] = [];
  let byteOffset = 0;

  const pushSection = (bytes: Uint8Array): number => {
    const viewIndex = bufferViews.length;
    bufferViews.push({ buffer: 0, byteOffset, byteLength: bytes.length, target: 34962 });
    sections.push(bytes);
    byteOffset += bytes.length;
    return viewIndex;
  };

  const pushMeshAccessor = (mesh: Mesh): number => {
    const view = pushSection(f32Bytes(mesh.verts));
    const idx = accessors.length;
    accessors.push({
      bufferView: view,
      componentType: 5126, // FLOAT
      count: mesh.vertexCount,
      type: 'VEC3',
      min: min3(mesh.verts),
      max: max3(mesh.verts),
    });
    return idx;
  };

  const bodyPos = pushMeshAccessor(body);
  const rotorPos = rotor ? pushMeshAccessor(rotor) : -1;

  // Animation accessors (time input + quaternion output) for the rotor spin.
  let inputAccessor = -1;
  let outputAccessor = -1;
  if (animated) {
    const steps = ROTOR_REVOLUTIONS * 4; // 4 keyframes / revolution
    const times: number[] = [];
    const quats: number[] = [];
    for (let i = 0; i <= steps; i++) {
      times.push((i / 4) * ROTOR_PERIOD_SECONDS);
      const q = quatZ((i / 4) * 2 * Math.PI);
      quats.push(q[0], q[1], q[2], q[3]);
    }
    const tView = pushSection(f32Bytes(times));
    inputAccessor = accessors.length;
    accessors.push({
      bufferView: tView,
      componentType: 5126,
      count: times.length,
      type: 'SCALAR',
      min: [times[0]],
      max: [times[times.length - 1]],
    });
    const qView = pushSection(f32Bytes(quats));
    outputAccessor = accessors.length;
    accessors.push({ bufferView: qView, componentType: 5126, count: times.length, type: 'VEC4' });
  }

  // Concatenate sections into one buffer.
  const total = byteOffset;
  const merged = new Uint8Array(total);
  let cursor = 0;
  for (const s of sections) {
    merged.set(s, cursor);
    cursor += s.length;
  }

  const { r, g, b } = parseHex(color);
  const material = {
    pbrMetallicRoughness: { baseColorFactor: [r, g, b, 1], metallicFactor: 0, roughnessFactor: 1 },
    doubleSided: true,
    extensions: { KHR_materials_unlit: {} },
  };

  const meshes: unknown[] = [
    { primitives: [{ attributes: { POSITION: bodyPos }, material: 0 }] },
  ];
  const nodes: unknown[] = [{ name: 'body', mesh: 0 }];
  const sceneNodes = [0];
  if (rotor) {
    meshes.push({ primitives: [{ attributes: { POSITION: rotorPos }, material: 0 }] });
    nodes.push({ name: 'rotor', mesh: 1, translation: [0, 0, HELI_ROTOR_HEIGHT] });
    sceneNodes.push(1);
  }

  const json: Record<string, unknown> = {
    asset: { version: '2.0', generator: 'tsr-aircraft-models' },
    extensionsUsed: ['KHR_materials_unlit'],
    scene: 0,
    scenes: [{ nodes: sceneNodes }],
    nodes,
    meshes,
    materials: [material],
    accessors,
    bufferViews,
    buffers: [
      { byteLength: total, uri: `data:application/octet-stream;base64,${base64(merged)}` },
    ],
  };

  if (animated) {
    json.animations = [
      {
        name: 'rotor-spin',
        channels: [{ sampler: 0, target: { node: 1, path: 'rotation' } }],
        samplers: [{ input: inputAccessor, output: outputAccessor, interpolation: 'LINEAR' }],
      },
    ];
  }

  const uri = `data:model/gltf+json;base64,${base64(new TextEncoder().encode(JSON.stringify(json)))}`;
  return { json, uri };
}

// Cache by archetype+colour+demo — the same airframe/role/mode reuses one model.
// Demo-mode is in the key so the rotor-spin variant never bleeds across modes.
const cache = new Map<string, string>();

/**
 * Resolve a tinted glTF model `data:` URI for an aircraft kind + role colour.
 * The helicopter rotor-spin clip is baked only in demo-mode (`isDemoMode()`).
 * Cached, so high-density LOD swaps are free after the first build.
 */
export function aircraftModelUri(kind: AircraftKind, color: string): string {
  const archetype = archetypeForKind(kind);
  const demo = isDemoMode();
  const key = `${archetype}|${color}|${demo ? 'demo' : 'op'}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const { uri } = buildGltf(archetype, color, demo);
  cache.set(key, uri);
  return uri;
}

/**
 * Test seam: the parsed glTF object for an archetype+colour (not cached).
 * `demo` controls whether the rotor-spin animation is baked (defaults to the
 * live `isDemoMode()` resolution).
 */
export function __buildAircraftGltf(
  archetype: AircraftModelArchetype,
  color: string,
  demo: boolean = isDemoMode(),
): unknown {
  return buildGltf(archetype, color, demo).json;
}

export function __clearModelCache(): void {
  cache.clear();
}
